import React, { useState, useMemo } from 'react';
import { 
  ChevronUp, 
  ChevronDown, 
  Filter, 
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  X
} from 'lucide-react';

const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  searchable = true,
  filterable = true,
  sortable = true,
  selectable = false,
  onRowClick = null,
  onRowSelect = null,
  onSort = null,
  onSearch = null,
  onFilter = null,
  actions = [],
  className = '',
  emptyMessage = 'No data available',
  searchPlaceholder = 'Search...',
  pageSize = 10,
  showPagination = true,
  compact = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Filter and search data
  const filteredData = useMemo(() => {
    // Ensure data is always an array
    const dataArray = Array.isArray(data) ? data : [];

    let filtered = dataArray;

    if (searchTerm && onSearch) {
      const searchResult = onSearch(dataArray, searchTerm);
      filtered = Array.isArray(searchResult) ? searchResult : dataArray;
    } else if (searchTerm) {
      filtered = dataArray.filter(item =>
        columns.some(column =>
          column.searchable !== false &&
          String(item[column.key] || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    return filtered;
  }, [data, searchTerm, onSearch, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    // Ensure filteredData is always an array
    const dataArray = Array.isArray(filteredData) ? filteredData : [];
    
    if (!sortConfig.key) return dataArray;

    return [...dataArray].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    // Ensure sortedData is always an array
    const dataArray = Array.isArray(sortedData) ? sortedData : [];
    
    if (!showPagination) return dataArray;
    
    const startIndex = (currentPage - 1) * pageSize;
    return dataArray.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize, showPagination]);

  const totalPages = Math.ceil((Array.isArray(sortedData) ? sortedData : []).length / pageSize);

  const handleSort = (key) => {
    if (!sortable) return;

    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    
    if (onSort) {
      onSort(key, direction);
    }
  };

  const handleSearch = (event) => {
    const value = event.target ? event.target.value : event;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleRowSelect = (row, isSelected) => {
    if (!selectable) return;

    let newSelectedRows;
    if (isSelected) {
      newSelectedRows = [...selectedRows, row];
    } else {
      newSelectedRows = selectedRows.filter(item => item.id !== row.id);
    }
    
    setSelectedRows(newSelectedRows);
    
    if (onRowSelect) {
      onRowSelect(newSelectedRows);
    }
  };

  const handleSelectAll = (isSelected) => {
    if (!selectable) return;

    const newSelectedRows = isSelected ? paginatedData : [];
    setSelectedRows(newSelectedRows);
    
    if (onRowSelect) {
      onRowSelect(newSelectedRows);
    }
  };

  const renderCell = (item, column) => {
    if (column.render) {
      return column.render(item, column);
    }

    const value = item[column.key];
    
    if (column.type === 'boolean') {
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }

    if (column.type === 'status') {
      const statusConfig = {
        active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
        inactive: { color: 'bg-red-100 text-red-800', icon: XCircle },
        pending: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
        verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
        rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
      };

      const config = statusConfig[value] || statusConfig.pending;
      const Icon = config.icon;

      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
          <Icon className="w-3 h-3 mr-1" />
          {value}
        </span>
      );
    }

    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }

    if (column.type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    }

    return value || '-';
  };

  const renderSortIcon = (columnKey) => {
    if (!sortable || !columnKey) return null;

    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? 
        <ChevronUp className="w-4 h-4" /> : 
        <ChevronDown className="w-4 h-4" />;
    }

    return <div className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Mobile card view component
  const MobileCard = ({ item, index }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm">
      {selectable && (
        <div className="flex items-center mb-3">
          <input
            type="checkbox"
            checked={selectedRows.some(row => row.id === item.id)}
            onChange={(e) => handleRowSelect(item, e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-gray-600">Select</span>
        </div>
      )}
      
      <div className="space-y-3">
        {columns.map((column) => (
          <div key={column.key} className="flex flex-col sm:flex-row sm:items-center">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 sm:mb-0 sm:w-1/3">
              {column.label}
            </div>
            <div className="text-sm text-gray-900 sm:w-2/3">
              {renderCell(item, column)}
            </div>
          </div>
        ))}
        
        {actions.length > 0 && (
          <div className="pt-3 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {actions.map((action, actionIndex) => (
                <button
                  key={actionIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick(item);
                  }}
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    action.variant === 'danger' 
                      ? 'text-red-700 bg-red-50 hover:bg-red-100' 
                      : action.variant === 'success'
                      ? 'text-green-700 bg-green-50 hover:bg-green-100'
                      : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                  }`}
                  title={action.title}
                >
                  {action.icon && (
                    typeof action.icon === 'function' 
                      ? (() => {
                          const IconComponent = action.icon(item);
                          return IconComponent ? <IconComponent className="w-3 h-3 mr-1" /> : null;
                        })()
                      : <action.icon className="w-3 h-3 mr-1" />
                  )}
                  {typeof action.label === 'function' ? action.label(item) : action.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header with search and filters */}
      {(searchable || filterable) && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            {searchable && (
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder={searchPlaceholder}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  {searchTerm && (
                    <button
                      onClick={() => handleSearch('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {filterable && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile View - Card Layout */}
      <div className="block lg:hidden">
        {paginatedData.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {emptyMessage}
          </div>
        ) : (
          <div className="p-4">
            {paginatedData.map((item, index) => (
              <MobileCard key={item.id || index} item={item} index={index} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop View - Table Layout */}
      <div className="hidden lg:block">
        {/* Table Container with proper overflow handling */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                {selectable && (
                  <th className={`${compact ? 'px-3' : 'px-6'} py-3 text-left`}>
                    <input
                      type="checkbox"
                      checked={selectedRows.length === paginatedData.length && paginatedData.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </th>
                )}
                
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`${compact ? 'px-3' : 'px-4'} py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      column.width || ''
                    } ${
                      sortable && column.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''
                    }`}
                    onClick={() => column.sortable !== false && handleSort(column.key)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.label}</span>
                      {renderSortIcon(column.key)}
                    </div>
                  </th>
                ))}
                
                {actions.length > 0 && (
                  <th className={`${compact ? 'px-3' : 'px-4'} py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-48`}>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.length === 0 ? (
                <tr>
                  <td 
                    colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)}
                    className={`${compact ? 'px-3' : 'px-6'} py-12 text-center text-gray-500`}
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr
                    key={item.id || index}
                    className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick && onRowClick(item)}
                  >
                    {selectable && (
                      <td className={`${compact ? 'px-3 py-3' : 'px-6 py-4'}`}>
                        <input
                          type="checkbox"
                          checked={selectedRows.some(row => row.id === item.id)}
                          onChange={(e) => handleRowSelect(item, e.target.checked)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    
                    {columns.map((column) => (
                      <td key={column.key} className={`${compact ? 'px-3 py-3' : 'px-4 py-3'} text-sm text-gray-900 ${column.width || ''} ${column.key === 'total_amount' ? 'text-right' : ''}`}>
                        {renderCell(item, column)}
                      </td>
                    ))}
                    
                    {actions.length > 0 && (
                      <td className={`${compact ? 'px-3 py-3' : 'px-4 py-3'} text-right text-sm font-medium w-1/6`}>
                        <div className="flex items-center justify-end space-x-1">
                          {actions.filter(action => typeof action.show !== 'function' || action.show(item)).map((action, actionIndex) => (
                            <button
                              key={actionIndex}
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick(item);
                              }}
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                                action.variant === 'danger' 
                                  ? 'text-red-700 hover:bg-red-100' 
                                  : action.variant === 'success'
                                  ? 'text-green-700 hover:bg-green-100'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              title={action.title}
                            >
                              {action.icon && (
                                typeof action.icon === 'function' 
                                  ? (() => {
                                      const IconComponent = action.icon(item);
                                      return IconComponent ? <IconComponent className="w-3 h-3 mr-1" /> : null;
                                    })()
                                  : <action.icon className="w-3 h-3 mr-1" />
                              )}
                              {typeof action.label === 'function' ? action.label(item) : action.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-700 text-center sm:text-left">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </button>
              
              <div className="flex items-center space-x-1">
                {(() => {
                  const maxVisiblePages = 7;
                  const pages = [];
                  
                  if (totalPages <= maxVisiblePages) {
                    // Show all pages if total is less than max visible
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Show first page
                    pages.push(1);
                    
                    if (currentPage > 4) {
                      pages.push('...');
                    }
                    
                    // Show pages around current page
                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(totalPages - 1, currentPage + 1);
                    
                    for (let i = start; i <= end; i++) {
                      if (i !== 1 && i !== totalPages) {
                        pages.push(i);
                      }
                    }
                    
                    if (currentPage < totalPages - 3) {
                      pages.push('...');
                    }
                    
                    // Show last page
                    if (totalPages > 1) {
                      pages.push(totalPages);
                    }
                  }
                  
                  return pages.map((page, index) => {
                    if (page === '...') {
                      return (
                        <span key={`ellipsis-${index}`} className="px-2 py-1 text-sm text-gray-500">
                          ...
                        </span>
                      );
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          currentPage === page
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">Next</span>
              </button>
              
              {/* Go to page input for large datasets */}
              {totalPages > 10 && (
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-sm text-gray-500">Go to:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        setCurrentPage(page);
                      }
                    }}
                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
